import { useAuth } from '@/lib/auth';
import { vocative } from 'czech-vocative';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AgentType, InquiryPublicDto, InquiryStatus, agentDefaultScopeForAccountType } from '@paladin-monorepo/common';
import { inquiriesApi } from '@/lib/api/inquiries';
import { matchingApi } from '@/lib/api/matching';
import { llmChatApi } from '@/lib/api/llm-chat';
import { pickDefaultAgentType } from '@/lib/pick-default-agent-config';
import { AppLayout } from '@/layouts/AppLayout';
import { isConsultantOrSolutionProvider, isInquirerAccount, isPrince2Account } from '@/lib/permissions';
import { dashboardV2Enabled } from '@/config/featureFlags';
import { Loading } from '@/components/Loading';
import { InquirerDashboardSection } from '@/components/dashboard/inquirer/InquirerDashboardSection';
import { SupplierDashboardSection } from '@/components/dashboard/supplier/SupplierDashboardSection';
import { HonoraryFlagBadge } from '@/components/account/HonoraryFlagBadge';
import { HonoraryFlagLead } from '@/components/account/HonoraryFlagLead';
import { DashboardHeroSection } from '@/components/dashboard/DashboardHeroSection';
import { DashboardMetricCards } from '@/components/dashboard/DashboardMetricCards';
import {
  getActiveProjectsForTable,
  getFeaturedPublishedInquiryId,
} from '@/components/dashboard/inquirer/inquirer-dashboard-data';
import { lazy, Suspense, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { trackEvent } from '@/lib/analytics';
import { getAppFormatLocale } from '@/utils/locale';

const PortfolioDashboard = lazy(() => import('@/components/dashboard/portfolio/PortfolioDashboard'));

export const DashboardPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'DashboardPage',
  });
  const { t } = useTranslation();

  const ssoLoginTracked = useRef(false);

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (!user || ssoLoginTracked.current) {
      return;
    }
    if (ref === 'linkedin-login') {
      ssoLoginTracked.current = true;
      trackEvent('login', { method: 'linkedin', sia: user.sia });
      navigate('/', { replace: true });
    }
    if (ref === 'google-login') {
      ssoLoginTracked.current = true;
      trackEvent('login', { method: 'google', sia: user.sia });
      navigate('/', { replace: true });
    }
  }, [searchParams, user, navigate]);

  const isSupplier = isConsultantOrSolutionProvider(user);
  const isInquirer = isInquirerAccount(user);
  const useHeroLayout = isInquirer || isSupplier;
  const usePortfolio = dashboardV2Enabled && isPrince2Account(user);

  const { data: agentConfigs } = useQuery({
    queryKey: ['agent-configs'],
    queryFn: () => llmChatApi.getAgentConfigs(),
    enabled: useHeroLayout,
  });

  const heroAgentType = useMemo(() => {
    const scope = agentDefaultScopeForAccountType(user?.account?.accountType);
    return pickDefaultAgentType(
      agentConfigs,
      isSupplier ? AgentType.Generic : AgentType.InquirerGuide,
      scope,
    );
  }, [agentConfigs, isSupplier, user?.account?.accountType]);

  const { data: inquiries, isLoading } = useQuery<InquiryPublicDto[]>({
    queryKey: ['inquiries'],
    queryFn: inquiriesApi.getInquiries,
    enabled: (isSupplier || isInquirer) && !usePortfolio,
  });

  const featuredInquiryId = useMemo(
    () => getFeaturedPublishedInquiryId(inquiries ?? []),
    [inquiries],
  );

  const { data: candidateMatches = [] } = useQuery({
    queryKey: ['matching', 'candidates', featuredInquiryId],
    queryFn: async ({ queryKey }) => {
      const id = queryKey[2];
      if (typeof id !== 'string') {
        return [];
      }
      return matchingApi.getCandidateMatches(id);
    },
    enabled: Boolean(featuredInquiryId) && isInquirer && !usePortfolio,
  });

  const showInquiriesLoading = (isSupplier || isInquirer) && isLoading;

  const welcomeName = user?.party?.firstName
    ? user.party.firstName.charAt(0).toUpperCase() +
      vocative(user.party.firstName).slice(1)
    : '';

  const activeProjects = useMemo(
    () => getActiveProjectsForTable(inquiries ?? []),
    [inquiries],
  );

  const nextMilestone = useMemo(() => {
    const deadlines = activeProjects
      .map((inquiry) => inquiry.deadline)
      .filter(Boolean)
      .map((value) => new Date(value as string))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());

    return deadlines[0]
      ? new Intl.DateTimeFormat(getAppFormatLocale(), {
          day: '2-digit',
          month: '2-digit',
        }).format(deadlines[0])
      : '-';
  }, [activeProjects]);

  const inquirerMetrics = useMemo(() => {
    const list = inquiries ?? [];
    return [
      {
        label: t('dashboard.inquirer.summary.activeProjects'),
        value: activeProjects.length,
      },
      {
        label: t('dashboard.inquirer.summary.publishedInquiries'),
        value: list.filter((inquiry) => inquiry.status === InquiryStatus.Published).length,
      },
      {
        label: t('dashboard.inquirer.summary.recommendedExperts'),
        value: candidateMatches.length,
      },
      {
        label: t('dashboard.inquirer.summary.nextMilestone'),
        value: nextMilestone,
        highlight: true,
      },
    ];
  }, [activeProjects.length, candidateMatches.length, inquiries, nextMilestone, t]);

  const portfolioSuggestions = [
    t('dashboard.portfolio.ask.suggestion1'),
    t('dashboard.portfolio.ask.suggestion2'),
    t('dashboard.portfolio.ask.suggestion3'),
  ];

  const honoraryBadge = (
    <HonoraryFlagBadge
      honoraryFlag={user?.account?.honoraryFlag}
      icon="experts"
      className="rounded-full border border-white/30 bg-transparent px-3 py-1.5 font-label uppercase text-surface-alt hover:bg-transparent"
    />
  );

  let body: ReactNode = null;
  if (usePortfolio) {
    body = (
      <Suspense fallback={<Loading text={t('generic.loading')} />}>
        <PortfolioDashboard scopeLabel={user?.account?.party?.name} />
      </Suspense>
    );
  } else if (isInquirer) {
    body = (
      <>
        {user?.account?.honoraryFlag && <HonoraryFlagLead />}

        {showInquiriesLoading && <Loading text={t('generic.loading')} />}

        {!isLoading && <InquirerDashboardSection inquiries={inquiries ?? []} />}
      </>
    );
  } else if (isSupplier) {
    body = (
      <>
        {user?.account?.honoraryFlag && <HonoraryFlagLead />}

        {showInquiriesLoading && <Loading text={t('generic.loading')} />}

        {!isLoading && <SupplierDashboardSection inquiries={inquiries ?? []} />}
      </>
    );
  }

  return (
    <AppLayout
      hideHeader={useHeroLayout}
      contentClassName={
        useHeroLayout
          ? 'flex min-w-0 flex-1 flex-col overflow-hidden bg-card p-0 md:rounded-md'
          : 'flex flex-1 flex-col gap-4 p-4'
      }
    >
      {useHeroLayout ? (
        <div className="flex min-w-0 flex-1 flex-col">
          <DashboardHeroSection
            welcomeName={welcomeName}
            honoraryBadge={honoraryBadge}
            agentType={heroAgentType}
            suggestions={usePortfolio ? portfolioSuggestions : undefined}
            className={isInquirer ? 'pb-8 sm:pb-10' : undefined}
          />

          <div className="flex min-w-0 flex-1 flex-col gap-5 bg-card px-5 pb-6 pt-5 sm:px-6">
            {isInquirer && !usePortfolio && !isLoading && (
              <div className="grid gap-3">
                <h2 className="font-label text-text-subtle md:hidden">
                  {t('dashboard.inquirer.summary.sectionTitle')}
                </h2>
                <DashboardMetricCards items={inquirerMetrics} />
              </div>
            )}
            {body}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-row items-center gap-5">
            <h2 className="text-display-2 text-brand-secondary">
              {t('dashboard.welcome')}, {welcomeName}
            </h2>
            <HonoraryFlagBadge honoraryFlag={user?.account?.honoraryFlag} />
          </div>

          {user?.account?.honoraryFlag && <HonoraryFlagLead />}
        </div>
      )}
    </AppLayout>
  );
};
